import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { request, requestVoid, toSlotDto, type Warning } from '@/shared/api'
import {
  type TimeBlock,
  type TimeBlockDraft,
  type TimeBlockPatch,
  TimeBlockResultSchema,
  TimeBlocksSchema,
} from '../model/schema'

export const timeBlocksQueryKey = ['time-blocks'] as const

export interface TimeBlocksQuery {
  from: Date
  to: Date
  employeeIds?: string[]
}

/**
 * Закрытое время читается своим запросом, а не приезжает вместе с делами: сетка и
 * так собирается из независимых чтений, а список дел про блоки знать не должен.
 */
export function timeBlocksQueryOptions(params: TimeBlocksQuery) {
  const query = {
    from: params.from.toISOString(),
    to: params.to.toISOString(),
    employee_ids: params.employeeIds ?? [],
  }
  return queryOptions({
    queryKey: [...timeBlocksQueryKey, query] as const,
    queryFn: (): Promise<TimeBlock[]> => request('/time-blocks', TimeBlocksSchema, { query }),
  })
}

export interface TimeBlockResult {
  time_block: TimeBlock
  warnings: Warning[]
}

export function useOccupyTimeMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (draft: TimeBlockDraft): Promise<TimeBlockResult> =>
      request('/time-blocks', TimeBlockResultSchema, {
        method: 'POST',
        body: {
          ...(draft.employeeId ? { employee_id: draft.employeeId } : {}),
          slot: toSlotDto(draft.slot),
          reason: draft.reason,
          note: draft.note,
        },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: timeBlocksQueryKey }),
  })
}

/**
 * Одна ручка на сдвиг и смену причины: «не выходной, а полдня занят» приходит
 * одной правкой. Интервал снять нельзя — у блока он обязателен.
 */
export function useMoveTimeBlockMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TimeBlockPatch }): Promise<TimeBlockResult> =>
      request(`/time-blocks/${id}`, TimeBlockResultSchema, {
        method: 'PATCH',
        body: {
          ...(patch.slot === undefined ? {} : { slot: toSlotDto(patch.slot) }),
          ...(patch.reason === undefined ? {} : { reason: patch.reason }),
          ...(patch.note === undefined ? {} : { note: patch.note }),
        },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: timeBlocksQueryKey }),
  })
}

export function useReleaseTimeMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string): Promise<void> =>
      requestVoid(`/time-blocks/${id}`, { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: timeBlocksQueryKey }),
  })
}
